import React from 'react';
import './styles.scss';
import withSizes from 'react-sizes';
import { titleCase } from '../../../utils/string';
import { Link } from 'react-router-dom';

const HEADER_HEIGHT = 220;
const DEFAULT_TIERS = [1, 2, 3, 4];

const PrivateLabelSupplyChainCountry = ({ shortname, isMobile, isIE }) => {
    const factoriesRef = React.useRef();
    const millsRef = React.useRef();
    const dyeHousesRef = React.useRef();
    const printHousesRef = React.useRef();

    const [currentTier, setCurrentTier] = React.useState(1);

    const { factories, mills, dyeHouses, printHouses } = React.useMemo(() => {
        return utils.getCountryStats(shortname);
    }, [shortname]);

    // Lookup the country name from the data, or title case the shortname if it fails
    const countryName = React.useMemo(() => {
        const fallback = titleCase(shortname.replace(/-/g, ''));
        const country = utils.getCountryFromSupplyChainData(shortname);
        if (country) return country.text;
        else return fallback;
    }, [shortname]);

    // Given a tier number, get it's ref
    const getTierRef = tier => {
        if (!tier) return null;
        const refs = [null, factoriesRef, millsRef, dyeHousesRef, printHousesRef];
        const ref = refs[tier];
        if (!ref || !ref.current) return null;
        return ref.current;
    };

    const scrollToTier = React.useCallback(tier => {
        const ref = getTierRef(tier);
        const scrollPos = ref.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
        window.scrollTo({
            top: scrollPos,
            behavior: 'auto',
        });
    }, []);

    // On load, scroll to the right tier if it's in the url like #tier-3
    React.useEffect(() => {
        setTimeout(() => {
            const { hash } = window.location;
            if (hash) {
                const tierNumber = hash.replace('#tier-', '');
                scrollToTier(tierNumber);
            }
        }, 100);
    }, [scrollToTier]);

    // Which tiers to show quick links for, based on if there's any facilities of each kind
    const quickLinkTiers = React.useMemo(() => {
        return [factories, mills, dyeHouses, printHouses].reduce((result, current, index) => {
            if (current.length > 0) return [...result, index + 1];
            else return result;
        }, []);
    }, [factories, mills, dyeHouses, printHouses]);

    // When the user scrolls, highlight the active quick link
    React.useEffect(() => {
        const scrollListener = () => {
            // Find the first tier that's in view
            const activeTier = DEFAULT_TIERS.findIndex(
                tier => utils.checkVisible(getTierRef(tier)) === true
            );
            // Set the active tier
            // If none of the tiers are active then the user must have scrolled past all the content,
            // So set the active tier to the last available one
            setCurrentTier(activeTier > -1 ? activeTier + 1 : DEFAULT_TIERS.length);
        };
        window.addEventListener('scroll', scrollListener);
        return () => window.removeEventListener('scroll', scrollListener);
    }, [setCurrentTier]);

    return (
        <div className="supply-chain-country">
            <PageHeader
                countryName={countryName}
                availableTiers={quickLinkTiers}
                currentTier={currentTier}
                onTierClicked={tier => scrollToTier(tier)}
                isIE={isIE}
            />
            <div className="tier-sections">
                {factories.length > 0 && (
                    <div id="tier-1" className="tier-section" ref={factoriesRef}>
                        <div className="tier-header">
                            <span className="tier-title">Tier 1 Factory</span>
                            <span className="updated-indicator">List Updated Twice Per Year</span>
                        </div>
                        <div className="tier-items factories">
                            <TableHeader isMobile={isMobile} tier={1} />
                            {factories.map(factory => (
                                <TableRow {...factory} isMobile={isMobile} />
                            ))}
                        </div>
                    </div>
                )}
                {mills.length > 0 && (
                    <div id="tier-2" className="tier-section" ref={millsRef}>
                        <div className="tier-header">
                            <span className="tier-title">Tier 2 Mill/Tannery</span>
                        </div>
                        <div className="tier-items mill-houses">
                            <TableHeader isMobile={isMobile} tier={2} />
                            {mills.map(mill => (
                                <TableRow {...mill} isMobile={isMobile} />
                            ))}
                        </div>
                    </div>
                )}
                {dyeHouses.length > 0 && (
                    <div id="tier-3" className="tier-section" ref={dyeHousesRef}>
                        <div className="tier-header">
                            <span className="tier-title">Tier 3 Dye House</span>
                        </div>
                        <div className="tier-items dye-houses">
                            <TableHeader isMobile={isMobile} tier={3} />
                            {dyeHouses.map(d => (
                                <TableRow {...d} isMobile={isMobile} />
                            ))}
                        </div>
                    </div>
                )}
                {printHouses.length > 0 && (
                    <div id="tier-4" className="tier-section" ref={printHousesRef}>
                        <div className="tier-header">
                            <span className="tier-title">Tier 4 Print House</span>
                        </div>
                        <div className="tier-items print-houses">
                            <TableHeader isMobile={isMobile} tier={4} />
                            {printHouses.map(p => (
                                <TableRow {...p} isMobile={isMobile} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div
                className="country-image"
                role="presentation"
                style={{
                    backgroundImage: `url('https://files.jdplc.com/images/private-label-supply-chain/${shortname}.jpg')`,
                }}
            />
        </div>
    );
};

const mapSizesToProps = ({ width }) => ({
    isMobile: width <= 1024,
});

export default withSizes(mapSizesToProps)(PrivateLabelSupplyChainCountry);

const PageHeader = ({
    countryName,
    availableTiers = DEFAULT_TIERS,
    currentTier,
    onTierClicked,
    isIE,
}) => {
    return (
        <div className="header">
            <div className="title">
                <Link to="/private-label-supply-chain">Private Label Supply Chain</Link>
                <span className="divider"> / </span>
                <span className="name">{countryName}</span>
            </div>
            {!isIE && (
                <div className="quick-links">
                    {availableTiers.length > 0 && <span className="indicator">Quick Link</span>}
                    {availableTiers.map(t => (
                        <span
                            className={`quick-link ${currentTier === t ? 'current' : ''}`}
                            onClick={() => onTierClicked(t)}
                            key={t}
                        >
                            Tier {t}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const TableHeader = ({ isMobile, tier }) => {
    if (isMobile) return null;
    return (
        <div className="tier-item tier-item--header">
            <div className="partner-name tier-item-label">Partner Name</div>
            <div className="partner-address tier-item-label">Partner Address</div>
            <div className="city tier-item-label">City</div>
            <div className="male-workers tier-item-label">{tier === 1 ? 'Male Workers' : ''}</div>
            <div className="female-workers tier-item-label">
                {tier === 1 ? 'Female Workers' : ''}
            </div>
            <div className="total-workers tier-item-label">{tier === 1 ? 'Total Workers' : ''}</div>
        </div>
    );
};

const TableRow = ({
    className = '',
    isMobile,
    name,
    address,
    city,
    maleWorkers = 0,
    femaleWorkers = 0,
    totalWorkers = maleWorkers + femaleWorkers,
}) => {
    if (!isMobile) {
        return (
            <div className={`tier-item tier-item--body ${className}`} key={name}>
                <div className="partner-name tier-item-value">{name.trim()}</div>
                <div className="partner-address tier-item-value">{address.trim()} </div>
                <div className="city tier-item-value">{city.trim()}</div>
                <div className="male-workers tier-item-value">
                    {totalWorkers > 0 && maleWorkers}
                </div>
                <div className="female-workers tier-item-value">
                    {totalWorkers > 0 && femaleWorkers}
                </div>
                <div className="total-workers tier-item-value">
                    {totalWorkers > 0 && totalWorkers}
                </div>
            </div>
        );
    } else {
        return (
            <>
                <div className="tier-item tier-item--body" key={name}>
                    <div className="partner-name tier-item-label">Partner Name</div>
                    <div className="partner-address tier-item-label">Partner Address</div>
                    <div className="partner-name tier-item-value">{name.trim()}</div>
                    <div className="partner-address tier-item-value">{address.trim()} </div>
                    <div className="city tier-item-label">City</div>
                    <div className="male-workers tier-item-label">
                        {totalWorkers > 0 && (
                            <>
                                Male <span>(Workers)</span>
                            </>
                        )}
                    </div>
                    <div className="female-workers tier-item-label">
                        {totalWorkers > 0 && 'Female Workers'}
                    </div>
                    <div className="total-workers tier-item-label">
                        {totalWorkers > 0 && 'Total Workers'}
                    </div>
                    <div className="city tier-item-value">{city.trim()}</div>
                    <div className="male-workers tier-item-value">
                        {totalWorkers > 0 && maleWorkers}
                    </div>
                    <div className="female-workers tier-item-value">
                        {totalWorkers > 0 && femaleWorkers}
                    </div>
                    <div className="total-workers tier-item-value">
                        {totalWorkers > 0 && totalWorkers}
                    </div>
                </div>
            </>
        );
    }
};

const utils = {
    getCountryFromSupplyChainData: country => {
        try {
            const allCountries = require('../private-label-supply-chain/data.json').items;
            return allCountries.find(c => c.name === country);
        } catch (e) {
            return null;
        }
    },
    getCountryStats: country => {
        return require(`./data/${country}.json`);
    },
    checkVisible: el => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
        return !(rect.bottom - HEADER_HEIGHT < 0 || rect.top - (viewHeight - HEADER_HEIGHT) >= 0);
    },
};
